/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import { ClassicEditor as ClassicEditorBase } from '@ckeditor/ckeditor5-editor-classic';

import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { CKFinderUploadAdapter } from '@ckeditor/ckeditor5-adapter-ckfinder';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
import { Bold, Italic } from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { CKBox } from '@ckeditor/ckeditor5-ckbox';
import { CKFinder } from '@ckeditor/ckeditor5-ckfinder';
import { EasyImage } from '@ckeditor/ckeditor5-easy-image';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload, PictureEditing } from '@ckeditor/ckeditor5-image';
import { Indent } from '@ckeditor/ckeditor5-indent';
import { Link } from '@ckeditor/ckeditor5-link';
import { List } from '@ckeditor/ckeditor5-list';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import { Table, TableToolbar } from '@ckeditor/ckeditor5-table';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import { CloudServices } from '@ckeditor/ckeditor5-cloud-services';
import { Markdown } from '@ckeditor/ckeditor5-markdown-gfm';

import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed';
import {Superscript} from '@ckeditor/ckeditor5-basic-styles';
import {Strikethrough} from '@ckeditor/ckeditor5-basic-styles';
import {Underline} from '@ckeditor/ckeditor5-basic-styles';
import {ImageResize} from '@ckeditor/ckeditor5-image';
import {IndentBlock} from '@ckeditor/ckeditor5-indent';
import {LinkImage} from '@ckeditor/ckeditor5-link';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';

import BalloonToolbar from '@ckeditor/ckeditor5-ui/src/toolbar/balloon/balloontoolbar';
import UploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';

import ObvioLeaderboardLink from './obvio-leaderboard-link';
import ObvioVariableSelector from './obvio-variable-selector';
import FontColor from './obvio-font/src/fontcolor';
import FontBackgroundColor from './obvio-font/src/fontbackgroundcolor';
import CustomFontFamily from './obvio-font/src/fontfamily/index';
import ObvioCustomButton from './obvio-custom-button/obviocustombutton';
import CustomButtonToolBar from './obvio-custom-button/custombuttontoolbar';
import CustomButtonMergeCodeLinks from './obvio-custom-button/custombuttonlink/custombuttonmergecodelinks';
import CustomTheme from './custom-theme';
import CustomFontSize from'./obvio-font/src/fontsize';
import { ELEMENT_NAME } from './obvio-variable-selector/Value';

export default class ClassicEditor extends ClassicEditorBase {
	public static override builtinPlugins = [
		Essentials,
		CKFinderUploadAdapter,
		Autoformat,
		Bold,
		Italic,
		BlockQuote,
		CKBox,
		CKFinder,
		CloudServices,
		EasyImage,
		Heading,
		Image,
		ImageCaption,
		ImageStyle,
		ImageToolbar,
		ImageUpload,
		Indent,
		Link,
		List,
		MediaEmbed,
		Paragraph,
		PasteFromOffice,
		PictureEditing,
		Table,
		TableToolbar,
		TextTransformation,
		Markdown,
		Alignment,
		HtmlEmbed,
		Superscript,
		Strikethrough,
		UploadAdapter,
		Underline,
		ImageResize,
		IndentBlock,
		LinkImage,
		RemoveFormat,
		BalloonToolbar,
		ObvioLeaderboardLink,
		ObvioVariableSelector,
		FontColor,
		FontBackgroundColor,
		CustomFontFamily,
		ObvioCustomButton,
		CustomButtonToolBar,
		CustomButtonMergeCodeLinks,
		CustomTheme,
		CustomFontSize,
	];

	public static override defaultConfig = {
		toolbar: {
			items: [
				'undo', 'redo',
				'|', 'heading',
				'|', 'bold', 'italic',
				'|', 'link', 'uploadImage', 'insertTable', 'blockQuote', 'mediaEmbed',
				'|', 'bulletedList', 'numberedList', 'outdent', 'indent'
			]
		},
		image: {
			toolbar: [
				'imageStyle:inline',
				'imageStyle:block',
				'imageStyle:side',
				'|',
				'toggleImageCaption',
				'imageTextAlternative'
			]
		},
		table: {
			contentToolbar: [
				'tableColumn',
				'tableRow',
				'mergeTableCells'
			]
		},
		// This value must be kept in sync with the language defined in webpack.config.js.
		language: 'en'
	};
}
